import React, { useState, useEffect, useMemo } from 'react';
import DropdownSelect from '../../../components/DropdownSelect';
import './ProductCard.css';

export function ProductCard({ product, onSelect, userMembership, onManageSubscription, onBuyLocal, onEditClick, showAdminEdit, linkedProductId, accessTag, productAccessTags }) {
  const productId = (product?.uniqueId || '').toLowerCase();
  const isFreeProduct = productId === 'free';
  const isLocalProduct = productId === 'local';
  const isOneTimeOnly = isFreeProduct || isLocalProduct;

  // Pro/Max: per-card billing (monthly | annual). Free/Local: always one-time.
  const getInitialBillingCycle = () => {
    if (isOneTimeOnly) return 'one-time';
    const productMatches = userMembership?.planId &&
      userMembership.planId.toLowerCase() === (product?.uniqueId || '').toLowerCase();
    if (productMatches && userMembership?.billingCycle) {
      const bc = userMembership.billingCycle.toLowerCase();
      if (bc === 'monthly' || bc === 'annual') return bc;
    }
    return 'monthly';
  };
  const [billingCycle, setBillingCycle] = useState(getInitialBillingCycle);
  const effectiveBillingCycle = isOneTimeOnly ? 'one-time' : billingCycle;

  // Get tiers from product
  const tiers = useMemo(() => product.tiers || [], [product.tiers]);
  const hasMultipleTiers = tiers.length > 1;

  // Initialize with first tier
  const [selectedTierId, setSelectedTierId] = useState(
    tiers.length > 0 ? tiers[0].id : null
  );

  // Find selected tier
  const selectedTier = tiers.find(t => t.id === selectedTierId) || tiers[0];

  // Check if this product is the user's current plan
  const productMatches = userMembership?.planId &&
    userMembership.planId.toLowerCase() === product.uniqueId?.toLowerCase();

  const tierMatches = !userMembership?.tierId || !selectedTier?.id ||
    userMembership.tierId.toLowerCase() === selectedTier.id.toLowerCase();

  const billingCycleMatches = isOneTimeOnly
    ? true
    : (userMembership?.billingCycle &&
        userMembership.billingCycle.toLowerCase() === effectiveBillingCycle?.toLowerCase());

  const isCurrentPlan = userMembership && productMatches && tierMatches && billingCycleMatches;

  const hasLocalAccessTag = isLocalProduct && accessTag && Array.isArray(productAccessTags) &&
    productAccessTags.some((t) => String(t || '').toLowerCase() === String(accessTag || '').toLowerCase());

  useEffect(() => {
    if (!productMatches) return;
    const membershipTierId = userMembership?.tierId;
    if (!membershipTierId) return;
    if (!tiers || tiers.length === 0) return;

    const targetTier = tiers.find(
      (t) => (t?.id || '').toLowerCase() === String(membershipTierId).toLowerCase()
    );
    if (!targetTier?.id) return;

    if (selectedTierId !== targetTier.id) {
      setSelectedTierId(targetTier.id);
    }
  }, [productMatches, tiers, userMembership?.tierId, selectedTierId]);

  useEffect(() => {
    if (tiers.length > 0 && !tiers.find(t => t.id === selectedTierId)) {
      setSelectedTierId(tiers[0].id);
    }
  }, [tiers, selectedTierId]);

  const calculatePrice = (monthlyPrice, billingCycle, discount = 0) => {
    const basePrice = parseFloat(monthlyPrice) || 0;
    const discountPercent = parseFloat(discount) || 0;

    if (billingCycle === 'monthly') {
      return basePrice;
    } else if (billingCycle === 'annual') {
      return basePrice * 12 * (1 - discountPercent / 100);
    } else if (billingCycle === 'one-time') {
      return basePrice * 12 * 3;
    }
    return basePrice;
  };

  const monthlyPrice = parseFloat(selectedTier?.price || 0);
  const discount = parseFloat(product.discount || 0);
  const calculatedPrice = isLocalProduct
    ? monthlyPrice
    : calculatePrice(monthlyPrice, effectiveBillingCycle, discount);

  const productFeatures = product.description
    ? product.description.split('\n').filter(line => line.trim().startsWith('-')).map(feature => ({
        text: feature,
        source: 'product'
      }))
    : [];

  const tierFeatures = selectedTier?.description
    ? selectedTier.description.split('\n').filter(line => line.trim().startsWith('-')).map(feature => ({
        text: feature,
        source: 'tier'
      }))
    : [];

  const features = [...productFeatures, ...tierFeatures];

  const tierOptions = tiers.map(tier => ({
    value: tier.id,
    label: tier.displayName || tier.id,
  }));

  const billingOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'annual', label: 'Annual' },
  ];

  const handleTierChange = (tierId) => {
    setSelectedTierId(tierId);
  };

  const handleSelect = (e) => {
    if (e.target.closest('.dropdown-select') || e.target.closest('button') || e.target.closest('.product-card-admin-edit')) {
      return;
    }

    if (isCurrentPlan && onManageSubscription) {
      onManageSubscription();
    } else if (isLocalProduct && onBuyLocal && linkedProductId) {
      onBuyLocal(linkedProductId);
    } else if (onSelect && selectedTier) {
      onSelect({
        planId: product.uniqueId,
        tierId: selectedTier.id,
        billingCycle: effectiveBillingCycle,
        price: calculatedPrice,
        monthlyPrice,
        discount,
      });
    }
  };

  return (
    <div
      className="product-card"
      style={{
        width: '320px',
        height: '550px',
        flexShrink: 0,
        boxSizing: 'border-box',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '24px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      onClick={handleSelect}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 0, 0, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {showAdminEdit && onEditClick && isLocalProduct && (
        <button
          type="button"
          className="product-card-admin-edit"
          onClick={(e) => {
            e.stopPropagation();
            onEditClick();
          }}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: 'rgba(0, 0, 0, 0.4)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
          aria-label="Edit Local card"
        >
          <i className="fa-solid fa-gear" style={{ fontSize: '14px' }} />
        </button>
      )}
      <div style={{ flexShrink: 0 }}>
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '600',
                color: '#fff',
              }}
            >
              {product.displayName}
            </h3>
            {selectedTier && selectedTier.displayName !== 'Basic' && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  background: 'rgba(139, 0, 0, 0.3)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#ff6b6b',
                  fontWeight: '500',
                }}
              >
                {selectedTier.displayName}
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
              <span
                style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#fff',
                  lineHeight: '1',
                }}
              >
                ${calculatedPrice.toFixed(2)}
              </span>
              <span
                style={{
                  fontSize: '16px',
                  color: '#999',
                  fontWeight: '400',
                }}
              >
                {isOneTimeOnly
                  ? ''
                  : effectiveBillingCycle === 'annual'
                    ? '/year'
                    : effectiveBillingCycle === 'monthly'
                      ? '/month'
                      : ' one-time'}
              </span>
            </div>
            {!isOneTimeOnly && effectiveBillingCycle === 'annual' && discount > 0 && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#4ade80',
                  fontWeight: '500',
                }}
              >
                Save ${(monthlyPrice * 12 - calculatedPrice).toFixed(2)}/year ({discount}% off)
              </div>
            )}
            {isOneTimeOnly && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#999',
                  fontWeight: '400',
                }}
              >
                one-time
              </div>
            )}
          </div>

          {selectedTier && selectedTier.credits > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: 'rgba(139, 0, 0, 0.12)',
                borderRadius: '6px',
                border: '1px solid rgba(139, 0, 0, 0.2)',
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#ff6b6b',
                  lineHeight: '1',
                }}
              >
                {selectedTier.credits.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#aaa',
                  fontWeight: '400',
                }}
              >
                credits/month
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className="product-card-description-scroll"
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          marginBottom: '20px',
        }}
      >
        {features.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            {features.map((feature, index) => (
              <li
                key={index}
                style={{
                  padding: '4px 0',
                  fontSize: '14px',
                  color: '#ccc',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '8px',
                }}
              >
                <span style={{
                  color: feature.source === 'tier' ? '#ff6b6b' : '#0f0',
                  fontSize: '16px',
                  fontWeight: feature.source === 'tier' ? 'bold' : 'normal'
                }}>
                  {feature.source === 'tier' ? '+' : '✓'}
                </span>
                <span>{feature.text.replace(/^-\s*/, '')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ flexShrink: 0 }}>
        {(hasMultipleTiers || !isOneTimeOnly) && (
          <div
            className="product-card-dropdowns-row"
            style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {hasMultipleTiers && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <DropdownSelect
                  value={selectedTierId}
                  options={tierOptions}
                  onValueChange={handleTierChange}
                  placeholder="Select Tier"
                  size="sm"
                  className="product-card-tier-select"
                  menuClassName="product-card-tier-menu"
                />
              </div>
            )}
            {!isOneTimeOnly && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <DropdownSelect
                  value={billingCycle}
                  options={billingOptions}
                  onValueChange={(val) => setBillingCycle(val)}
                  placeholder="Billing"
                  size="sm"
                  className="product-card-tier-select"
                  menuClassName="product-card-tier-menu"
                />
              </div>
            )}
          </div>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isCurrentPlan && onManageSubscription) {
              onManageSubscription();
            } else if (isLocalProduct && onBuyLocal && linkedProductId) {
              onBuyLocal(linkedProductId);
            } else if (onSelect && selectedTier) {
              onSelect({
                planId: product.uniqueId,
                tierId: selectedTier.id,
                billingCycle: effectiveBillingCycle,
                price: calculatedPrice,
                monthlyPrice,
                discount,
              });
            }
          }}
          style={{
            width: '100%',
            padding: '12px 24px',
            background: isCurrentPlan
              ? 'rgba(100, 100, 100, 0.3)'
              : hasLocalAccessTag
                ? 'rgba(34, 197, 94, 0.5)'
                : 'rgba(139, 0, 0, 0.8)',
            border: isCurrentPlan
              ? '1px solid rgba(100, 100, 100, 0.5)'
              : hasLocalAccessTag
                ? '1px solid rgba(34, 197, 94, 0.8)'
                : '1px solid rgba(139, 0, 0, 1)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!isCurrentPlan) {
              if (hasLocalAccessTag) {
                e.currentTarget.style.background = 'rgba(34, 197, 94, 0.7)';
              } else {
                e.currentTarget.style.background = 'rgba(139, 0, 0, 1)';
              }
            }
          }}
          onMouseLeave={(e) => {
            if (!isCurrentPlan) {
              if (hasLocalAccessTag) {
                e.currentTarget.style.background = 'rgba(34, 197, 94, 0.5)';
              } else {
                e.currentTarget.style.background = 'rgba(139, 0, 0, 0.8)';
              }
            }
          }}
        >
          {isCurrentPlan
            ? 'Manage Plan'
            : hasLocalAccessTag
              ? 'Purchased'
              : isLocalProduct
                ? 'Buy Local'
                : 'Select Plan'}
        </button>
      </div>
    </div>
  );
}
